import React from "react";
import { Box, Stack, Typography, Skeleton } from "@mui/material";
import { Card, SectionTitle, ActionButton, StatusBadge, relativeDate, T } from "./dashboardUtils";

function ActivitySkeleton() {
  return (
    <Stack spacing={1}>
      {[...Array(5)].map((_, i) => (
        <Box
          key={i}
          sx={{
            px: 2,
            py: 1.25,
            borderRadius: 2,
            border: `1px solid ${T.border}`,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 1.5,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ flex: 1 }}>
            <Skeleton variant="rounded" width={36} height={36} sx={{ borderRadius: 2, flexShrink: 0 }} />
            <Skeleton variant="text" width={140} height={16} />
          </Stack>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Skeleton variant="rounded" width={80} height={20} sx={{ borderRadius: 10 }} />
            <Skeleton variant="text" width={65} height={14} />
          </Stack>
        </Box>
      ))}
    </Stack>
  );
}

export default function RecentActivityCard({ isLoading, recentActivity, onViewAll }) {
  return (
    <Card sx={{ mb: 2.5 }}>
      <SectionTitle
        title="Recent Activity"
        subtitle="Latest updates across all data brokers"
        action={<ActionButton label="View All" onClick={onViewAll} />}
      />
      <Box sx={{ p: 2.5 }}>
        {isLoading ? (
          <ActivitySkeleton />
        ) : recentActivity.length === 0 ? (
          <Typography sx={{ fontSize: "0.9rem", color: T.faint, textAlign: "center", py: 3 }}>
            No recent activity found.
          </Typography>
        ) : (
          <Stack spacing={1}>
            {recentActivity.map((item, i) => {
              const initials = (item.company ?? "?")
                .split(/(?=[A-Z])|\s/)
                .filter((w) => w.length > 0)
                .slice(0, 2)
                .map((w) => w[0].toUpperCase())
                .join("");
              return (
                <Box
                  key={item.request_id}
                  sx={{
                    bgcolor: i % 2 === 0 ? "#FAFBFE" : "transparent",
                    px: 2,
                    py: 1.25,
                    borderRadius: 2,
                    border: `1px solid ${T.border}`,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 1.5,
                    transition: "background 0.2s, border-color 0.2s",
                    flexWrap: "wrap",
                    "&:hover": { bgcolor: T.accentBg, borderColor: "#D1D5DB" },
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={1.5} sx={{ flex: 1, minWidth: 0 }}>
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: 2,
                        bgcolor: "#FFFFFF",
                        border: `1px solid ${T.border}`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <Typography sx={{ fontSize: 12, fontWeight: 700, color: T.ink }}>
                        {initials}
                      </Typography>
                    </Box>
                    <Typography
                      variant="body2"
                      sx={{ fontSize: "0.875rem", fontWeight: 600, color: T.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                    >
                      {item.company ?? "—"}
                    </Typography>
                  </Stack>
                  <Stack direction="row" alignItems="center" spacing={1.5} sx={{ flexShrink: 0 }}>
                    <StatusBadge status={item.latest_status} />
                    <Typography
                      variant="caption"
                      sx={{ fontSize: "0.75rem", color: T.faint, minWidth: 70, textAlign: "right" }}
                    >
                      {relativeDate(item.updated_at || item.created_at)}
                    </Typography>
                  </Stack>
                </Box>
              );
            })}
          </Stack>
        )}
      </Box>
    </Card>
  );
}
